import { PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps extends PropsWithChildren {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
}

export function Modal({ isOpen, onClose, children }: ModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [portalContainer] = useState(() => {
    const div = document.createElement('div');
    return div;
  });

  useEffect(() => {
    document.body.appendChild(portalContainer);
    
    return () => {
      if (portalContainer.parentNode === document.body) {
        document.body.removeChild(portalContainer);
      }
    };
  }, [portalContainer]);

  useEffect(() => {
    if (!isOpen) return;

    // Handler per la chiusura con ESC
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Aggiungi l'event listener
    document.addEventListener('keydown', handleEscape);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handler per il click sul backdrop
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Chiudi solo se il click è sul backdrop e non sul contenuto del modale
    if (modalContentRef.current && !modalContentRef.current.contains(event.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div 
      className="fixed inset-0 overlay-in z-40 flex items-center justify-center bg-black/40"
      onClick={handleBackdropClick}
    >
      <div className="max-h-[90vh] overflow-y-auto" ref={modalContentRef}>
        <div className="bg-white shadow-xl">{children}</div>
      </div>
    </div>,
    portalContainer,
  );
}

export default Modal;
